import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AccordionModule } from 'primeng/accordion';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ChartModule } from 'primeng/chart';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { ImageModule } from 'primeng/image';
import { ProgressBarModule } from 'primeng/progressbar';
import { RatingModule } from 'primeng/rating';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TooltipModule } from 'primeng/tooltip';
import { Currency, getCurrencyDescription } from '../../../models/enums/UserRole';
import { InstituteCommonDetailsV2Response } from '../../../models/institute/instituteCommonDetailsV2Response';
import { InstituteUnderGraduateDetailsV2Response } from '../../../models/institute/instituteUnderGraduateDetailsV2Response';
import { InstituteMasterDetailsV2Response } from '../../../models/institute/InstituteMasterDetailsV2Response';
import { InstituteTotalEnrollmentsV2Response } from '../../../models/institute/instituteTotalEnrollmentsV2Response';
import { InstituteService } from '../../../services/institute/institute.service';
import { InstituteDetailAlumniComponent } from '../institute-detail-alumni/institute-detail-alumni.component';

interface InstituteRaceEthnicityData {
  fulltimeEnrollment: number | null;
  maleEnrollment: string | null;
  femaleEnrollment: string | null;
}

@Component({
  selector: 'app-institute-detail-in',
  standalone: true,
  imports: [CommonModule, FormsModule, CheckboxModule, ImageModule, BreadcrumbModule,
    RouterModule, ChipModule, TooltipModule, AccordionModule, DropdownModule, ProgressBarModule,
    RatingModule, ScrollPanelModule, ChartModule, InstituteDetailAlumniComponent],
  templateUrl: './institute-detail-in.component.html',
  styleUrl: './institute-detail-in.component.scss'
})
export class InstituteDetailInComponent implements OnChanges {
  private instituteService = inject(InstituteService);

  @Input() instituteUGMDetails!: InstituteUnderGraduateDetailsV2Response | InstituteMasterDetailsV2Response;
  instituteMajors: any;
  @Input() institute: InstituteCommonDetailsV2Response | undefined;
  @Input() selectedProgramType: string | undefined;
  popularEmployers?: string | null;
  instituteBachelorMajors: InstituteTotalEnrollmentsV2Response[] = [];
  instituteGraduateMajors: InstituteTotalEnrollmentsV2Response[] = [];
  instituteDoctoralMajors: InstituteTotalEnrollmentsV2Response[] = [];
  raceData: InstituteRaceEthnicityData | null = null;
  pieData: any;
  pieOptions: any;
  @ViewChild('chart1') chart1: any;
  @Output() requestDisplayCourses = new EventEmitter<void>();
  scholarships: string[] = [];
  showAllScholarships = false;
  showAllUGMajors = false;
  showAllPGMajors = false;
  showAllDoctoralMajors = false;

  get visibleScholarships() {
    return this.showAllScholarships ? this.scholarships : this.scholarships.slice(0, 3);
  }
  toggleScholarshipsView() {
    this.showAllScholarships = !this.showAllScholarships;
  }


  get visibleUGMajors() {
    return this.getVisibleMajors(this.instituteBachelorMajors, this.showAllUGMajors);
  }
  get visiblePGMajors() {
    return this.getVisibleMajors(this.instituteGraduateMajors, this.showAllPGMajors);
  }
  get visibleDoctoralMajors() {
    return this.getVisibleMajors(this.instituteDoctoralMajors, this.showAllDoctoralMajors);
  }

  getVisibleMajors(courses: any[], showAll: boolean): any[] {
    return showAll ? courses : courses.slice(0, 3);
  }

  toggleMajorsView(key: 'UG' | 'PG' | 'Doctoral') {
    const prop = `showAll${key}Majors` as 'showAllUGMajors' | 'showAllPGMajors' | 'showAllDoctoralMajors';
    this[prop] = !this[prop];
  }

  triggerDisplayCourses() {
    this.requestDisplayCourses.emit();
  }

  ngOnChanges(): void {
    if (this.isUGDetails(this.instituteUGMDetails)) {
      this.popularEmployers = this.instituteUGMDetails.popularEmployers;
      this.scholarships = this.instituteUGMDetails.findScholarships || [];
    }
    this.listMajors();
    this.prepareChartData();
  }

  listMajors() {
    this.instituteService.listInstituteTotalEnrollments({
      instituteId: this.institute!.id,
      levelOfCrediential: 'Bachelor'
    }).subscribe({
      next: (response) => {
        this.instituteBachelorMajors = response.result;
      }
    });
    this.instituteService.listInstituteTotalEnrollments({
      instituteId: this.institute!.id,
      levelOfCrediential: 'Master'
    }).subscribe({
      next: (response) => {
        this.instituteGraduateMajors = response.result;
      }
    });
    this.instituteService.listInstituteTotalEnrollments({
      instituteId: this.institute!.id,
      levelOfCrediential: 'Doctoral'
    }).subscribe({
      next: (response) => {
        this.instituteDoctoralMajors = response.result;
      }
    })
  }

  getAmount(currency: Currency, amount: string | number): string {
    const amountNum = Number(amount);
    const formattedAmount = amountNum.toLocaleString('en-IN'); // or 'en-US' based on formatting needs
    return `${getCurrencyDescription(currency)} ${formattedAmount}`;
  }

  isUGDetails(details: InstituteUnderGraduateDetailsV2Response | InstituteMasterDetailsV2Response): details is InstituteUnderGraduateDetailsV2Response {
    return (
      details != null &&
      'totalApplicants' in details
    );
  }

  // ✅ Type guard for Masters
  isMastersDetails(details: InstituteUnderGraduateDetailsV2Response | InstituteMasterDetailsV2Response): details is InstituteMasterDetailsV2Response {
    return (
      details != null &&
      'graduateStudentsEnrolled' in details
    );
  }

  stringToPercent(amount: string | number): number {

    const amountNum = (Number(amount));

    return Number((amountNum * 100).toFixed(2));
  }

  normalizeToNumber(value: string | number | null): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      if (value.includes('%')) {
        return parseFloat(value.replace('%', ''));
      }
      return parseFloat(value); // for "7695" style
    }
    return 0;
  }

  prepareChartData() {
    if (this.isUGDetails(this.instituteUGMDetails)) {
      this.raceData = {
        fulltimeEnrollment: null,
        maleEnrollment: this.instituteUGMDetails?.fullTimeUndergraduateMaleEnrollment ?? null,
        femaleEnrollment: this.instituteUGMDetails?.fullTimeUndergraduateFemaleEnrollment ?? null,
      };
    } else if (this.isMastersDetails(this.instituteUGMDetails)) {
      this.raceData = {
        fulltimeEnrollment: null,
        maleEnrollment: this.toPercentValue(this.instituteUGMDetails?.maleGraduateEnrollment)?.toString() ?? null,
    femaleEnrollment: this.toPercentValue(this.instituteUGMDetails?.femaleGraduateEnrollment)?.toString() ?? null,
        // maleEnrollment: this.stringToPercent(this.instituteUGMDetails?.maleGraduateEnrollment ?? 0).toString() ?? null,
        // femaleEnrollment: this.stringToPercent(this.instituteUGMDetails?.femaleGraduateEnrollment ?? 0).toString() ?? null,
      };
    }

    if (this.raceData) {
      this.pieData = {
        labels: [
          'Male Enrollment',
          'Female Enrollment',
        ],
        datasets: [
          {
            data: [
              this.normalizeToNumber(this.raceData.maleEnrollment),
              this.normalizeToNumber(this.raceData.femaleEnrollment),
            ],
            backgroundColor: [
              '#CED12A',
              '#000086'
            ],
            hoverBackgroundColor: [
              '#CED12A',
              '#000086'
            ],
          },
        ],
      };

      this.pieOptions = {
        plugins: {
          legend: {

            position: 'top',
            labels: {
              color: '#495057',
            },
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.label || '';
                const data = context.dataset.data;
                const value = context.parsed;
                const total = data.reduce((sum: number, val: any) => sum + val, 0);
                const percentage = total ? ((value / total) * 100).toFixed(2) : 0;
                return `${label}: (${percentage}%)`;
              },
            },
          },
        },
      };

    }
  }
toPercentValue(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = this.normalizeToNumber(value as any);
  if (!isFinite(n)) return null;
  if (typeof value === 'string' && value.includes('%')) return n;
  return n > 0 && n <= 1 ? Number((n * 100).toFixed(2)) : n;
}

formatPercent(value: string | number | null | undefined): string {
  const p = this.toPercentValue(value);
  return p === null ? 'N/A' : `${p}%`;
}
}
